// src/modules/calculators/LightingCalculator.jsx
import { useState, useMemo, useEffect } from "react";
import { 
  Zap, LayoutGrid, FileDown, Info, 
  CheckCircle2, Monitor, MousePointer2 
} from "lucide-react"; 
import { 
  calculateLampsV2, FIXTURE_TYPES, LIGHT_SOURCES, LIGHTING_STANDARDS 
} from "../../utils/lightingFormulas";
import { motion, AnimatePresence } from "framer-motion";
import { generateLightingReport } from "../../services/reports/LightingReport";

const LightingCalculator = () => {
  const [inputData, setInputData] = useState({
    length: 5.5, width: 4, targetLux: 500, 
    lumens: 3860, watts: 35.2, 
    type: "panel", source: "led"
  });

  const [debouncedData, setDebouncedData] = useState(inputData);

  // Debounce para evitar lag en el renderizado del SVG
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedData(inputData), 300);
    return () => clearTimeout(handler);
  }, [inputData]);

  const results = useMemo(() => calculateLampsV2({
    length: Number(debouncedData.length),
    width: Number(debouncedData.width),
    targetLux: Number(debouncedData.targetLux),
    lumensPerLamp: Number(debouncedData.lumens),
    wattsPerLamp: Number(debouncedData.watts)
  }), [debouncedData]);

  const handleInput = (e) => {
    const { name, value } = e.target;
    setInputData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="grid gap-4 lg:grid-cols-12 lg:gap-8">
      
      {/* PANEL IZQUIERDO: CONFIGURACIÓN */}
      <div className="space-y-4 lg:col-span-4 lg:space-y-6">
        
        {/* SECCIÓN 1: MONITOR DE GEOMETRÍA */}
        <div className="rounded-xl border border-border bg-surface p-4 shadow-sm sm:rounded-2xl sm:p-6">
          <h3 className="mb-3 flex items-center gap-2 text-xs font-black uppercase tracking-tight text-brand-dark sm:mb-4 sm:text-sm">
            <Monitor size={16} className="text-brand-primary" /> Geometría del Espacio
          </h3>
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="text-[9px] font-bold text-gray-400 uppercase">Largo (m)</label>
              <input name="length" type="number" value={inputData.length} onChange={handleInput} className="w-full mt-1 p-2 bg-white border border-border rounded-lg outline-none font-bold text-brand-primary" />
            </div>
            <div>
              <label className="text-[9px] font-bold text-gray-400 uppercase">Ancho (m)</label>
              <input name="width" type="number" value={inputData.width} onChange={handleInput} className="w-full mt-1 p-2 bg-white border border-border rounded-lg outline-none font-bold text-brand-primary" />
            </div>
            <div className="col-span-2">
              <label className="text-[9px] font-bold text-gray-400 uppercase flex items-center gap-1">
                Requerimiento Normativo <Info size={10} className="text-brand-primary"/>
              </label>
              <select name="targetLux" value={inputData.targetLux} onChange={handleInput} className="w-full mt-1 p-2 bg-white border border-border rounded-lg text-xs font-bold">
                {LIGHTING_STANDARDS.map((std, i) => (
                  <option key={i} value={std.lux}>{std.area} ({std.lux} lx)</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* SECCIÓN 2: FUENTE LUMÍNICA (ZAP) */}
        <div className="rounded-2xl bg-brand-dark p-4 text-white shadow-xl sm:rounded-[32px] sm:p-6">
           <h4 className="mb-3 flex items-center gap-2 text-[10px] font-black uppercase text-brand-accent sm:mb-4">
             <Zap size={14} /> Fuente de Iluminación
           </h4>
           <div className="space-y-3 sm:space-y-4">
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="text-[9px] text-white/40 uppercase">Tipo</label>
                  <select name="type" value={inputData.type} onChange={handleInput} className="w-full mt-1 p-2 bg-white/10 border border-white/10 rounded-lg text-white text-[10px] outline-none">
                    {Object.entries(FIXTURE_TYPES).map(([k, v]) => <option key={k} value={k} className="text-black">{v.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[9px] text-white/40 uppercase">Fuente</label>
                  <select name="source" value={inputData.source} onChange={handleInput} className="w-full mt-1 p-2 bg-white/10 border border-white/10 rounded-lg text-white text-[10px] outline-none">
                    {Object.entries(LIGHT_SOURCES).map(([k, v]) => <option key={k} value={k} className="text-black">{v.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[9px] text-white/40 uppercase">Lúmenes/Lamp</label>
                  <input name="lumens" type="number" value={inputData.lumens} onChange={handleInput} className="w-full mt-1 p-2 bg-white/10 border border-white/10 rounded-lg text-white font-mono text-sm" />
                </div>
                <div>
                  <label className="text-[9px] text-white/40 uppercase">Vatios/Lamp</label>
                  <input name="watts" type="number" value={inputData.watts} onChange={handleInput} className="w-full mt-1 p-2 bg-white/10 border border-white/10 rounded-lg text-white font-mono text-sm" />
                </div>
              </div>
           </div>
        </div>
        
        {/* SECCIÓN 3: DIAGNÓSTICO (CHECKCIRCLE + ANIMATEPRESENCE) */}
        <AnimatePresence mode="wait">
          <motion.div 
            key={results?.achievedLux}
            initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 10 }}
            className="space-y-3 rounded-2xl border border-border bg-white p-4 shadow-sm sm:space-y-4 sm:rounded-[32px] sm:p-6"
          >
            <div className="flex justify-between items-center border-b border-brand-soft pb-2">
              <h4 className="text-[10px] font-black text-brand-primary uppercase">Cálculo de Eficiencia</h4>
              <CheckCircle2 size={14} className="text-brand-accent" />
            </div>
            <div className="space-y-3 text-xs">
              <div className="flex justify-between"><span>Cantidad Óptima:</span><span className="font-black text-lg">{results?.optimal}</span></div>
              <div className="flex justify-between"><span>Iluminancia Final:</span><span className="font-bold">{results?.achievedLux} lx</span></div>
              <div className="flex justify-between text-brand-primary font-bold"><span>Carga Específica:</span><span>{results?.wattsPerM2} W/m²</span></div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* PANEL DERECHO: PLANO TÉCNICO (LAYOUTGRID) */}
      <div className="lg:col-span-8 flex flex-col">
        <div className="relative flex min-h-[220px] flex-grow items-center justify-center rounded-2xl border-2 border-white bg-gray-50 p-4 shadow-xl sm:min-h-[320px] sm:rounded-[32px] sm:border-4 sm:p-6 md:min-h-[450px] md:rounded-[48px] md:p-10">
          
          <div className="relative flex max-w-[420px] w-full items-center justify-center border border-gray-100 bg-white p-6 shadow-2xl sm:p-10 md:p-12" 
               style={{ aspectRatio: `${inputData.width}/${inputData.length}` }}>
            
            {/* Reglas dinámicas */}
            <div className="absolute bottom-6 left-2 top-6 flex flex-col justify-between border-r border-gray-100 pr-2 text-[8px] font-bold text-gray-300 sm:bottom-12 sm:top-12">
               <span>0</span>
               {results?.coordsY.map((y, i) => <span key={i}>{y.toFixed(1)}</span>)}
               <span>{inputData.length}</span>
            </div>

            <svg viewBox={`0 0 ${inputData.width * 10} ${inputData.length * 10}`} className="h-full w-full">
              {results && results.coordsY.map((y, r) => 
                results.coordsX.map((x, c) => (
                  <motion.g key={`${r}-${c}`} initial={{ scale: 0 }} animate={{ scale: 1 }}>
                    {FIXTURE_TYPES[inputData.type].shape === "circle" ? (
                      <circle cx={x * 10} cy={y * 10} r="3.5" fill="#39ff14" className="drop-shadow-[0_0_8px_rgba(57,255,20,0.8)]" />
                    ) : (
                      <rect 
                        x={x * 10 - 5} y={y * 10 - 2} 
                        width={inputData.type === "tube_long" ? 12 : 6} 
                        height="4" fill="#39ff14" rx="0.5" 
                        className="drop-shadow-[0_0_8px_rgba(57,255,20,0.8)]"
                      />
                    )}
                  </motion.g>
                ))
              )}
            </svg>
            <div className="absolute -top-7 left-0 flex items-center gap-1.5 text-[9px] font-black uppercase tracking-wider text-brand-primary sm:-top-10 sm:gap-2 sm:text-[10px] sm:tracking-[0.3em]">
              <MousePointer2 size={12} /> Distribución: <LayoutGrid size={14} className="ml-0.5 text-brand-accent" /> {results?.cols} x {results?.rows}
            </div>
          </div>
        </div>

        <button 
            className="mt-4 flex w-full cursor-pointer items-center justify-center gap-2 rounded-2xl bg-brand-dark py-3.5 text-xs font-bold text-brand-accent shadow-xl transition-all hover:bg-black active:scale-95 sm:mt-6 sm:gap-3 sm:rounded-[24px] sm:py-5 sm:text-sm"
            onClick={() => generateLightingReport(inputData, results)}
            >
            <FileDown size={18}/> DESCARGAR REPORTE LUMÍNICO PDF
            </button>
      </div>
    </div>
  );
};

export default LightingCalculator;
/*<!--© 2026 Todos los derechos reservados Desarrollado por JEMAprod-Tecnologia-->*/